// Inspired by https://github.com/kanety/stimulus-dialog
import { Controller } from "@hotwired/stimulus";

const dialogName = (el) => el?.closest("[data-dialog-name]")?.dataset.dialogName;

export default class UIDialog extends Controller {
  static targets = ["dialog", "modal", "focus", "drag", "backdrop", "closeButton"];

  connect() {
    window.addEventListener("keydown", this.closeByKey.bind(this));

    this.modalTargets.forEach((modal) => {
      modal.addEventListener("click", this.closeByModal.bind(this));
    });

    this.#openFromQuery();
  }

  // ?dialog=<name> opens that dialog on load, so a link from elsewhere can land someone directly in it.
  #openFromQuery() {
    const url = new URL(window.location.href);
    const wanted = url.searchParams.get("dialog");
    if (!wanted) return;

    const dialog = this.dialogTargets.find((d) => dialogName(d) === wanted);
    if (!dialog) return;

    // Dropped from the URL once it has been acted on, or closing the dialog and reloading -- or coming
    // back to the page -- would reopen it.
    url.searchParams.delete("dialog");
    window.history.replaceState({}, "", url);
    this.openBy(dialog);
  }

  // Also fires for close buttons rendered after connect (e.g. inside lazy-loaded frames).
  closeButtonTargetConnected(button) {
    button.addEventListener("click", this.close.bind(this));
  }

  // A scope can host several dialogs (a dropdown with both Edit and Share). Triggers pick one by
  // data-dialog-name; unnamed triggers get the first dialog, which is what single-dialog scopes rely on.
  dialogFor(target) {
    const name = dialogName(target);
    if (!name) return this.dialogTarget;

    return this.dialogTargets.find((d) => dialogName(d) === name) || this.dialogTarget;
  }

  // Only this dialog's backdrop/modal, so opening one doesn't dim behind another.
  partnersFor(dialog) {
    const name = dialogName(dialog);
    const matches = (el) => !name || dialogName(el) === name;
    return { backdrops: this.backdropTargets.filter(matches), modals: this.modalTargets.filter(matches) };
  }

  disconnect() {
    window.removeEventListener("keydown", this.closeByKey.bind(this));
  }

  initialize() {}
  open(e) {
    this.openBy(e.target);
    e.preventDefault();
  }

  // For a trigger that is ALSO a link: open() calls preventDefault, which would cancel the very frame
  // navigation that fills the dialog. Opens the shell and lets the click through.
  openWithoutPreventing(e) {
    this.openBy(e.target);
  }

  close(e) {
    this.closeBy(e.target);
    e.preventDefault();
  }

  toggle(e) {
    if (this.isVisible(e.target)) {
      this.closeBy(e.target);
    } else {
      this.openBy(e.target);
    }
    e.preventDefault();
  }

  // Escape fires on window, so the event target is wherever focus happens to be — resolve the dialog
  // that is actually open instead of falling back to the first one.
  closeByKey(e) {
    if (e.keyCode != 27) return;

    const open = this.dialogTargets.find((d) => !d.classList.contains("hidden"));
    if (!open) return;

    this.closeBy(open);
    e.preventDefault();
  }

  closeByModal(e) {
    const dialog = this.dialogFor(e.currentTarget);
    if (!dialog.contains(e.target)) {
      this.closeBy(e.currentTarget);
    }
  }

  isVisible(target) {
    return this.dialogFor(target).classList.contains("st-dialog--visible");
  }

  openBy(target) {
    this.toggleClass(true, this.dialogFor(target));

    if (this.hasFocusTarget) {
      this.focusTarget.focus();
    }

    this.dispatch("opened", { detail: { target: target } });
  }

  closeBy(target) {
    const event = this.dispatch("before-close", { detail: { target }, cancelable: true });
    if (event.defaultPrevented) return;

    this.toggleClass(false, this.dialogFor(target));

    if (target.getAttribute("data-ui--dialog-target") === "modal") {
      document.body.classList.remove("overflow-hidden");
    }
    this.dispatch("closed", { detail: { target: target } });
  }

  // Refactor Me
  // This needs to be combined with the toggle method in sheet_controller
  toggleClass(visible, dialog = this.dialogTarget) {
    const { backdrops, modals } = this.partnersFor(dialog);
    const state = visible ? "open" : "closed";

    [dialog, ...backdrops, ...modals].forEach((el) => {
      el.classList.toggle("hidden", !visible);
      el.dataset.state = state;
    });
  }
}
